import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Plus } from "lucide-react";
import { VENTURES } from "../../data/ventures";
import { FadeUp, SectionLabel } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];

const VentureCard = ({ v, i }) => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 160, damping: 18 });
  const sry = useSpring(ry, { stiffness: 160, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 9);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 9);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
  <motion.div
    ref={ref}
    onMouseMove={onMove}
    onMouseLeave={onLeave}
    onClick={() => navigate(`/ventures/${v.id}`)}
    role="button"
    data-testid={`venture-card-${v.id}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    whileHover={{ y: -6 }}
    transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: EASE }}
    style={{ rotateX: srx, rotateY: sry, transformPerspective: 1100 }}
    className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-950/60 p-7 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 hover:border-cyan-400/40 hover:shadow-[0_0_36px_rgba(0,229,255,0.12)] ${
      v.span === 2 ? "lg:col-span-2" : ""
    }`}
  >
    <div
      className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      style={{
        background: `radial-gradient(480px at 30% 0%, ${v.accent}14, transparent 70%)`,
      }}
    />
    <div className="relative flex items-start justify-between">
      <div className="flex items-center gap-4">
        <img
          src={v.logo}
          alt={`${v.name} logo`}
          className="h-16 w-16 rounded-xl object-cover ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-105"
        />
        <div>
          <span
            className="font-mono-tech text-[0.62rem] uppercase tracking-[0.26em]"
            style={{ color: v.accent }}
          >
            {v.category}
          </span>
          <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-white">
            {v.name}
          </h3>
        </div>
      </div>
      <span className="font-mono-tech text-sm text-slate-600">{v.index}</span>
    </div>

    <p className="relative mt-4 font-display text-base font-medium text-slate-200">
      {v.tagline}
    </p>
    <p className="relative mt-3 flex-1 text-sm leading-relaxed text-slate-400">
      {v.description}
    </p>

    <div className="relative mt-6 flex flex-wrap gap-2">
      {v.tags.map((t) => (
        <span
          key={t}
          className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono-tech text-[0.62rem] uppercase tracking-wider text-slate-400"
        >
          {t}
        </span>
      ))}
    </div>

    <div className="relative mt-7 flex items-center justify-between">
      <span className="inline-flex items-center gap-1.5 font-display text-sm font-semibold text-cyan-300">
        View Details
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </span>
      <a
        href={v.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        data-testid={`venture-link-${v.id}`}
        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2.5 font-display text-sm font-semibold text-white transition-colors duration-300 hover:border-cyan-400/50 hover:bg-cyan-400/10"
      >
        Visit Website
        <ArrowUpRight className="h-4 w-4" />
      </a>
    </div>
  </motion.div>
  );
};

export const Ventures = () => (
  <section id="ventures" data-testid="ventures-section" className="relative py-24 md:py-36">
    <div className="pointer-events-none absolute left-0 top-60 h-96 w-96 rounded-full bg-cyan-500/10 blur-[150px]" />
    <div className="mx-auto max-w-[1440px] px-6 md:px-12">
      <FadeUp>
        <SectionLabel>The Portfolio</SectionLabel>
      </FadeUp>
      <div className="mt-6 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <FadeUp delay={0.1}>
          <h2 className="max-w-2xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Six ventures.
            <br />
            <span className="text-gradient-cyan">One relentless engine.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="max-w-md text-base leading-relaxed text-slate-400">
            Every company in the Veerox ecosystem is engineered around the same
            DNA — intelligence, automation and uncompromising quality.
          </p>
        </FadeUp>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {VENTURES.map((v, i) => (
          <VentureCard key={v.id} v={v} i={i} />
        ))}
        <motion.div
          data-testid="venture-card-next"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="group relative flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-transparent p-7 text-center transition-colors duration-300 hover:border-cyan-400/40"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] transition-transform duration-300 group-hover:scale-110">
            <Plus className="h-6 w-6 text-cyan-400" />
          </span>
          <p className="mt-5 font-display text-lg font-semibold text-white">
            The next venture is loading…
          </p>
          <p className="mt-2 font-mono-tech text-xs uppercase tracking-[0.22em] text-slate-500">
            Veerox never stops building
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);
